import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import {
  normalizeReaderPermissionStatus,
  normalizeZettleAvailability,
  normalizeZettleSessionIdentity,
} from '../utils/zettlePos';

/**
 * PayPal/Zettle Entrega 1: puente hacia el plugin nativo de cobros con lector.
 *
 * LA WEB NUNCA INTENTA ABRIR EL SDK: fuera de la app Android (o si el build no lo inicializó),
 * `checkZettleAvailability` responde `available: false` sin tocar el plugin, y la opción de cobro
 * con lector ni se pinta. No hay feature flag que pueda encenderla en la web desplegada.
 *
 * PAYPAL-GATE-02: el modo del SDK (Developer Mode o REAL) lo decide el BUILD de Android. La web solo
 * lo LEE para avisar al operador; ningún método del plugin acepta un modo, una lista de permisos ni
 * credenciales.
 */

/** Lo ÚNICO que cruza el puente: identificadores, montos y moneda. Jamás datos de tarjeta. */
export interface ZettleBridgePayload {
  outcome: 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'UNKNOWN';
  reference?: string;
  amountMinor?: number;
  transactionId?: string;
  /** Código ISO 4217 que reportó el SDK, cuando lo expone (retrieve sí; el resultado del cobro no). */
  currency?: string;
  reason?: string;
  detail?: string;
}

/** DEV = Developer Mode (sin dinero real) · REAL = build piloto de PAYPAL-GATE-02 · NONE = sin SDK. */
export type ZettleSdkMode = 'DEV' | 'REAL' | 'NONE';

export interface ZettleAvailability {
  available: boolean;
  /** true = Developer Mode (sin dinero real). */
  devMode: boolean;
  /** El modo que decidió el build de Android. Informativo: la web no puede cambiarlo. */
  mode: ZettleSdkMode;
  /** Solo en REAL: ¿el Payments SDK tiene sesión del comercio? */
  loggedIn?: boolean;
}

/** Estado mínimo de los permisos del lector. QUÉ permisos son lo decide el lado nativo. */
export type ZettleReaderPermissionStatus = 'GRANTED' | 'DENIED' | 'DENIED_PERMANENTLY';

/**
 * Deuda (n), primera mitad: quién es la sesión del SDK en ESTE equipo. Solo sufijos de 8 caracteres,
 * país y moneda — nunca el identificador completo, el nombre público, la imagen ni ningún token.
 */
export interface ZettleSessionIdentity {
  mode: ZettleSdkMode;
  loggedIn: boolean;
  organizationSuffix?: string;
  userSuffix?: string;
  country?: string;
  currency?: string;
}

interface ZettlePaymentsPluginContract {
  isAvailable(): Promise<Partial<ZettleAvailability>>;
  sessionIdentity(): Promise<Partial<ZettleSessionIdentity>>;
  charge(options: { attemptId: string; amountMinor: number }): Promise<ZettleBridgePayload>;
  retrieve(options: { attemptId: string }): Promise<ZettleBridgePayload>;
  ensureReaderPermissions(): Promise<{ status?: string }>;
  checkPermissions(): Promise<{ status?: string }>;
  login(): Promise<{ started?: boolean }>;
  openAppSettings(): Promise<void>;
  addListener(eventName: 'zettleAuthChange', listener: () => void): Promise<PluginListenerHandle>;
}

const plugin = registerPlugin<ZettlePaymentsPluginContract>('ZettlePayments');

export async function checkZettleAvailability(): Promise<ZettleAvailability> {
  // Primera guarda: en web no se pregunta siquiera — el plugin no existe ahí.
  if (!Capacitor.isNativePlatform()) return normalizeZettleAvailability(undefined);
  try {
    return normalizeZettleAvailability(await plugin.isAvailable());
  } catch {
    // Plugin no registrado en este build: no disponible, sin error visible.
    return normalizeZettleAvailability(undefined);
  }
}

/** Lanza la UI del SDK con el intento QUE ENTREGÓ EL BACKEND. Resuelve con la observación. */
export function chargeWithZettle(options: { attemptId: string; amountMinor: number }): Promise<ZettleBridgePayload> {
  return plugin.charge(options);
}

/** Consulta el SDK por NUESTRA referencia (recuperación de un intento en duda). */
export function retrieveWithZettle(options: { attemptId: string }): Promise<ZettleBridgePayload> {
  return plugin.retrieve(options);
}

/** PAYPAL-GATE-02: pide (si faltan) los permisos que el SDK exige al lector en modo REAL. */
export async function ensureZettleReaderPermissions(): Promise<ZettleReaderPermissionStatus> {
  const result = await plugin.ensureReaderPermissions();
  return normalizeReaderPermissionStatus(result?.status);
}

/**
 * Deuda (l): LEE el estado de los permisos del lector SIN pedir nada. El plugin responde con el
 * estado actual y jamás abre un diálogo de Android.
 *
 * NO confundir con `ensureZettleReaderPermissions`, que SÍ pide: llamarla al reanudar la app abriría
 * un diálogo en cada regreso y podría quemar el «no volver a preguntar» de Android, dejando el lector
 * inutilizable a media jornada.
 *
 * `undefined` = no se pudo saber (fuera de Android, plugin viejo o error). Quien llama debe DEJAR el
 * aviso como está: un puente que falla no puede borrar una advertencia.
 */
export async function checkZettleReaderPermissions(): Promise<ZettleReaderPermissionStatus | undefined> {
  if (!Capacitor.isNativePlatform()) return undefined;
  try {
    return normalizeReaderPermissionStatus((await plugin.checkPermissions())?.status);
  } catch {
    return undefined;
  }
}

/**
 * Deuda (n): quién tiene la sesión del SDK en este equipo. Mismo patrón tolerante que
 * `checkZettleAvailability`: el `catch` cubre el APK anterior, donde el método no existe, y responde
 * «sin SDK» en vez de romper la pantalla.
 */
export async function readZettleSessionIdentity(): Promise<ZettleSessionIdentity> {
  if (!Capacitor.isNativePlatform()) return normalizeZettleSessionIdentity(undefined);
  try {
    return normalizeZettleSessionIdentity(await plugin.sessionIdentity());
  } catch {
    return normalizeZettleSessionIdentity(undefined);
  }
}

/** PAYPAL-GATE-02: abre el inicio de sesión oficial del Payments SDK (solo build REAL). */
export async function startZettleLogin(): Promise<{ started: boolean }> {
  const result = await plugin.login();
  // P-015: `started: false` = el SDK ya tenía sesión y NO se abrió un segundo login.
  return { started: result?.started !== false };
}

/** P-015: abre los Ajustes de Android de ESTA app (permisos del lector), sin reinstalar. */
export async function openZettleAppSettings(): Promise<void> {
  await plugin.openAppSettings();
}

/**
 * P-015: el lado nativo avisa que la sesión del Payments SDK cambió (p. ej. terminó el login). El aviso
 * es solo un DISPARO: lo que traiga no se lee — quien escucha vuelve a preguntar `isAvailable()`.
 * Devuelve cómo dejar de escuchar. Fuera de Android no hace nada.
 */
export function onZettleAuthChange(listener: () => void): () => void {
  if (!Capacitor.isNativePlatform()) return () => undefined;
  let handle: PluginListenerHandle | undefined;
  let removed = false;
  void plugin.addListener('zettleAuthChange', () => listener()).then((registered) => {
    if (removed) void registered.remove();
    else handle = registered;
  }).catch(() => undefined);
  return () => {
    removed = true;
    void handle?.remove().catch(() => undefined);
  };
}
